import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { API_CONFIG } from '../lib/config'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Label } from '../components/ui/label'
import { Alert, AlertDescription } from '../components/ui/alert'
import { Loader2, Lock, User, Eye, EyeOff, Sparkles } from 'lucide-react'

export default function LoginPage() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    const navigate = useNavigate()
    const { login } = useAuthStore()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        setError('')

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error?.message || 'Giriş yapılamadı')
            }

            const data = await response.json()

            // AuthStore'a kullanıcı bilgilerini kaydet
            login(data.user, data.accessToken, data.refreshToken)

            // Ürünler sayfasına yönlendir
            navigate('/products')
        } catch (err: any) {
            setError(err.message || 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
                <div className="absolute top-40 left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </div>

            <div className="w-full max-w-md relative z-10">
                {/* Logo ve Başlık */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-2xl mb-6 shadow-2xl">
                        <Sparkles className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-3 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                        LUJO Toptan
                    </h1>
                    <p className="text-gray-300 text-lg">Stok Takip ve İstatistik Sistemi</p>
                </div>

                {/* Giriş Kartı */}
                <Card className="shadow-2xl border-0 bg-white/10 backdrop-blur-xl backdrop-saturate-150">
                    <CardHeader className="text-center pb-6">
                        <CardTitle className="text-3xl font-bold text-white">
                            Giriş Yap
                        </CardTitle>
                        <CardDescription className="text-gray-300 text-base">
                            Hesabınıza erişmek için bilgilerinizi girin
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Email Alanı */}
                            <div className="space-y-3">
                                <Label htmlFor="email" className="text-sm font-semibold text-gray-200">
                                    E-posta Adresi
                                </Label>
                                <div className="relative group">
                                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-400 transition-colors" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="admin@lujo.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-12 h-14 bg-white/20 border-white/20 text-white placeholder-gray-400 focus:bg-white/30 focus:border-purple-400 focus:ring-purple-400 transition-all duration-300 rounded-xl"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Şifre Alanı */}
                            <div className="space-y-3">
                                <Label htmlFor="password" className="text-sm font-semibold text-gray-200">
                                    Şifre
                                </Label>
                                <div className="relative group">
                                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-purple-400 transition-colors" />
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-12 pr-12 h-14 bg-white/20 border-white/20 text-white placeholder-gray-400 focus:bg-white/30 focus:border-purple-400 focus:ring-purple-400 transition-all duration-300 rounded-xl"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-purple-400 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Hata Mesajı */}
                            {error && (
                                <Alert variant="destructive" className="border-red-400/50 bg-red-500/20 text-red-200">
                                    <AlertDescription>
                                        {error}
                                    </AlertDescription>
                                </Alert>
                            )}

                            {/* Giriş Butonu */}
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="w-full h-14 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 hover:from-purple-700 hover:via-pink-700 hover:to-blue-700 text-white font-bold text-lg shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 rounded-xl transform hover:scale-105"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                                        Giriş Yapılıyor...
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="w-5 h-5 mr-3" />
                                        Giriş Yap
                                    </>
                                )}
                            </Button>
                        </form>


                    </CardContent>
                </Card>

                {/* Alt Bilgi */}
                <div className="text-center mt-8">
                    <p className="text-sm text-gray-400">
                        © 2024 LUJO Toptan. Tüm hakları saklıdır.
                    </p>
                </div>
            </div>
        </div>
    )
}
